/* eslint-disable no-restricted-syntax */
import React, { memo, useState, useEffect, ChangeEvent } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Grid, Theme } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import { withStyles, WithStyles, createStyles } from '@material-ui/core/styles';
import { AutocompleteChangeReason } from '@material-ui/lab';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { ActionModal } from '../../../../components/Modal';
import { IconTextField, TextField } from '../../../../components/TextField';
import { TimePicker } from '../../../../components/TimePicker';
import { Button, IconOnlyButton } from '../../../../components/Button';
import { generateRoomWithoutSeparator } from '../../../../helper/random';
import { isLowercaseAlphanumeric } from '../../../../helper/validation';
import { replaceRoomNameWithTitle } from '../../../../app/config';
import {
  IGetGroupResponse,
  IGroup,
  emptyGroup,
} from '../../../group/groups.slice';
import { IDevice } from '../../../devices/devices.slice';
import { IUser } from '../../../auth/auth.slice';
import { IScheduledMeetingData } from '../../scheduledEvent.api';
import { IScheduleMeetings } from '../../../scheduleMeeting/scheduleMeeting.slice';
import { Tab, TabPanel, Tabs } from '../../../../components/Tabs';
import { Typography } from '../../../../components/Typography';
import { AutocompleteTextField } from '../../../../components/AutocompleteTextField';
import { CopyIcon } from '../../../../assets/icons';

const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    datePicker: {
      margin: theme.spacing(2, 0),
      '& .MuiFormControl-root': {
        width: '100%',
      },
    },
    select: {
      width: '100%',
      color: '#989898',
      margin: '10px 0',
      '& em': {
        fontStyle: 'unset !important',
      },
      '& .MuiInputLabel-outlined': {
        transform: 'translate(14px, 30px) scale(1)',
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
    borderRight: {
      '& .MuiTab-wrapper': {
        borderRight: `1px solid ${theme.palette.grey[500]}`,
      },
    },
    borderLeft: {
      '& .MuiTab-wrapper': {
        borderLeft: `1px solid ${theme.palette.grey[500]}`,
      },
    },
    autocomplete: {
      '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
        padding: '0',
      },
    },
    streamerDescription: {
      marginTop: '12px',
      height: '103px',
    },
    copyWrapper: {
      position: 'relative',
    },
    copyButton: {
      position: 'absolute',
      right: 4,
      top: 16,
      zIndex: 1,
      '& .custom-style-icon-button': {
        border: 'none',
      },
    },
  });
};

export interface IScheduleMeetingModalProps extends WithStyles<typeof styles> {
  open: boolean;
  isUpdateScheduledEvent: boolean;
  meeting: IScheduleMeetings;
  groups: IGetGroupResponse;
  devicesList: IDevice[];
  membersList: IUser[];
  handleClose?: React.MouseEventHandler<HTMLButtonElement>;
  saveMeetingButtonClick: (data: IScheduledMeetingData) => void;
  loading: boolean;
  isCreateScheduledEvent: boolean;
  isViewScheduledEvent: boolean;
}

const CreateScheduledEventModal = ({
  classes,
  open,
  isUpdateScheduledEvent,
  meeting,
  groups,
  devicesList,
  membersList,
  handleClose,
  saveMeetingButtonClick,
  loading,
  isCreateScheduledEvent,
  isViewScheduledEvent,
}: IScheduleMeetingModalProps) => {
  const intl = useIntl();
  const [meetingName, setMeetingName] = useState({ value: '', error: '' });
  const [startTime, setStartTime] = useState({
    value: moment(new Date()).format('YYYY-MM-DDTHH:mm'),
    error: '',
  });
  const endDate = new Date();
  endDate.setTime(endDate.getTime() + 60 * 60 * 1000);
  const [endTime, setEndTime] = useState({
    value: moment(endDate).format('YYYY-MM-DDTHH:mm'),
    error: '',
  });

  interface IMemberState {
    error: boolean;
    value: IUser[];
  }
  interface IDeviceState {
    error: boolean;
    value: IDevice[];
  }

  const [members, setMembers] = useState<IMemberState>({
    error: false,
    value: [],
  });
  const [viewUrl, setViewUrl] = useState({ value: '', error: '' });
  const [group, setGroup] = useState({
    value: {
      ...emptyGroup,
      name: '',
    },
    error: '',
  });
  const [devices, setDevices] = useState<IDeviceState>({
    error: false,
    value: [],
  });

  useEffect(() => {
    if ((isUpdateScheduledEvent || isViewScheduledEvent) && meeting) {
      const startTimeError =
        new Date() > new Date(meeting?.beginTime)
          ? intl.formatMessage({
              id: 'enter_valid_date_time',
            })
          : '';
      const endTimeError =
        new Date() > new Date(meeting?.endTime)
          ? intl.formatMessage({
              id: 'enter_valid_date_time',
            })
          : '';

      setMeetingName({ value: meeting?.name, error: '' });
      setViewUrl({ value: meeting?.scheduledeventViewingUrl, error: '' });
      setStartTime({
        value: moment(meeting?.beginTime).format('YYYY-MM-DDTHH:mm'),
        error: startTimeError,
      });
      setEndTime({
        value: moment(meeting?.endTime).format('YYYY-MM-DDTHH:mm'),
        error: endTimeError,
      });
      if (meeting?.groupId) setGroup({ value: meeting.groupId, error: '' });

      const setMembersValueArr = [];
      for (const member1 of meeting?.members) {
        for (const member2 of membersList) {
          if (member1.userId._id === member2._id) {
            setMembersValueArr.push(member2);
          }
        }
      }
      setMembers({
        value: setMembersValueArr,
        error: setMembersValueArr.length === 0,
      });
      const setDevicesValueArr = [];
      for (const device1 of meeting?.devices) {
        for (const device2 of devicesList) {
          if (device1.deviceId._id === device2._id) {
            setDevicesValueArr.push(device2);
          }
        }
      }
      setDevices({
        value: setDevicesValueArr,
        error: setDevicesValueArr.length === 0,
      });

      setViewUrl({
        value: meeting?.scheduledevent?.viewingUrl,
        error: '',
      });
    }
  }, [
    isUpdateScheduledEvent,
    isViewScheduledEvent,
    membersList,
    devicesList,
    meeting,
  ]);

  const _handleNameChange = (e: { target: { value: string } }) => {
    setMeetingName({ value: e.target.value, error: '' });
  };

  const _handleViewUrlChange = (e: { target: { value: string } }) => {
    setViewUrl({ value: e.target.value, error: '' });
  };

  const _handleStartTimeChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    if (new Date() <= new Date(e.target.value)) {
      setStartTime({ value: e.target.value, error: '' });
      const date = new Date(e.target.value);
      date.setTime(date.getTime() + 60 * 60 * 1000); // set end time 1 hour after start time
      setEndTime({ value: moment(date).format('YYYY-MM-DDTHH:mm'), error: '' });
    } else
      setStartTime({
        value: e.target.value,
        error: intl.formatMessage({
          id: 'enter_valid_date_time',
        }),
      });
  };

  const _handleEndTimeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (new Date() <= new Date(e.target.value))
      setEndTime({ value: e.target.value, error: '' });
    else
      setEndTime({
        value: e.target.value,
        error: intl.formatMessage({
          id: 'enter_valid_date_time',
        }),
      });
  };

  // const _handleParticipantsChange = (e: any) => {
  //   setParticipants({ value: e.target.value, error: '' });
  // };

  const handleGroupChange = (
    event: ChangeEvent<unknown>,
    value: IGroup | null,
    reason: AutocompleteChangeReason
  ) => {
    if (value) {
      setGroup({
        value,
        error: '',
      });
    } else {
      setGroup({
        value: {
          ...emptyGroup,
          name: '',
        },
        error: '',
      });
    }
  };

  const handleDeviceChange = (
    event: ChangeEvent<unknown>,
    value: IDevice[],
    reason: AutocompleteChangeReason
  ) => {
    setDevices({
      value,
      error: value.length === 0,
    });
  };

  const _validate = () => {
    let isValidate = true;
    if (!meetingName.value) {
      isValidate = false;
      setMeetingName({
        ...meetingName,
        error: intl.formatMessage({
          id: 'meeting_name_required',
        }),
      });
    }

    if (startTime.error || endTime.error) isValidate = false;

    if (new Date(startTime.value) > new Date(endTime.value)) {
      isValidate = false;
      setEndTime({
        ...endTime,
        error: intl.formatMessage({
          id: 'end_time_cannot_be_before_begin_time',
        }),
      });
    }

    // URL validation
    const urlRegex = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/g;

    if (!urlRegex.test(viewUrl.value)) {
      setViewUrl({
        ...viewUrl,
        error: intl.formatMessage({
          id: 'enter_valid_url',
        }),
      });
      isValidate = false;
    }

    return isValidate;
  };

  const _saveMeetingButtonClick = () => {
    if (_validate()) {
      const memberIdsArr = [];
      const deviceIdsArr = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const member of members.value) {
        memberIdsArr.push(member._id);
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const device of devices.value) {
        deviceIdsArr.push(device._id);
      }

      let data: IScheduledMeetingData = {
        name: meetingName.value,
        beginTime: new Date(startTime.value),
        endTime: new Date(endTime.value),
        participantCount: 0,
        scheduledeventViewingUrl: viewUrl.value,
        groupId: group.value._id || null,
        deviceId: null,
        livestreamingGroupId: null,
        devices: deviceIdsArr,
        livestreamingDevices: [],
        members: [],
      };

      if (isUpdateScheduledEvent) data = { ...data, id: meeting?._id };

      saveMeetingButtonClick(data);
    }
  };

  const onClickCopy = () => {
    navigator.clipboard.writeText(viewUrl.value);
  };

  return (
    <ActionModal
      modalTitle={intl.formatMessage({
        id:
          (isCreateScheduledEvent && 'create_scheduled_event') ||
          (isUpdateScheduledEvent && 'update_scheduled_event') ||
          'view_scheduled_event',
      })}
      modalDescription={intl.formatMessage({
        id:
          isUpdateScheduledEvent || isCreateScheduledEvent
            ? 'create_scheduled_event_description'
            : 'view_scheduled_event_description',
      })}
      id="create-event-modal"
      open={open}
      handleClose={handleClose}
      actinButtonOnClick={_saveMeetingButtonClick}
      actinButtonLabel={intl.formatMessage({
        id: isCreateScheduledEvent ? 'create' : 'save',
      })}
      actionButtonDisabled={
        loading || !(isUpdateScheduledEvent || isCreateScheduledEvent)
      }
    >
      <TextField
        placeholder={intl.formatMessage({
          id: 'title',
        })}
        onChange={_handleNameChange}
        value={meetingName.value}
        required
        error={Boolean(meetingName.error)}
        helperText={meetingName.error}
        disabled={!(isUpdateScheduledEvent || isCreateScheduledEvent)}
      />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TimePicker
            label={intl.formatMessage({
              id: 'start_time',
            })}
            type="datetime-local"
            id="scheduled-event-start-time"
            onChange={(e) => _handleStartTimeChange(e)}
            value={startTime.value}
            error={!isViewScheduledEvent ? Boolean(startTime.error) : false}
            helperText={!isViewScheduledEvent ? startTime.error : null}
            disabled={!(isUpdateScheduledEvent || isCreateScheduledEvent)}
          />
        </Grid>
        <Grid item xs={6}>
          <TimePicker
            label={intl.formatMessage({
              id: 'end_time',
            })}
            type="datetime-local"
            id="schedule-meeting-end-time"
            onChange={(e) => _handleEndTimeChange(e)}
            value={endTime.value}
            error={!isViewScheduledEvent ? Boolean(endTime.error) : false}
            helperText={!isViewScheduledEvent ? endTime.error : null}
            disabled={!(isUpdateScheduledEvent || isCreateScheduledEvent)}
          />
        </Grid>
      </Grid>
      <div className={classes.copyWrapper}>
        <IconOnlyButton customStyles={classes.copyButton} onClick={onClickCopy}>
          <img src={CopyIcon} alt="copy" />
        </IconOnlyButton>
        <TextField
          placeholder={intl.formatMessage({
            id: 'streaming_url',
          })}
          onChange={_handleViewUrlChange}
          value={viewUrl.value}
          error={Boolean(viewUrl.error)}
          helperText={viewUrl.error}
          disabled={!(isUpdateScheduledEvent || isCreateScheduledEvent)}
          required
        />
      </div>

      <AutocompleteTextField
        multiple={false}
        limitTags={1}
        id="scheduled-event-groups"
        options={groups.data}
        getOptionLabel={(option) => option.name}
        value={group.value}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={handleGroupChange}
        disabled={!(isUpdateScheduledEvent || isCreateScheduledEvent)}
        label={intl.formatMessage({
          id: 'group',
        })}
        placeholder={intl.formatMessage({
          id: 'select_a_group',
        })}
      />
      <AutocompleteTextField
        multiple
        limitTags={2}
        id="scheduled-event-devices"
        options={devicesList}
        getOptionLabel={(option) => option.userDeviceName || option.deviceName}
        value={devices.value}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={handleDeviceChange}
        disabled={!(isUpdateScheduledEvent || isCreateScheduledEvent)}
        label={intl.formatMessage({
          id: 'devices',
        })}
      />
    </ActionModal>
  );
};

export default memo(withStyles(styles)(CreateScheduledEventModal));
